import type { OnErrorFn } from '@formatjs/intl';
import loadable, { lazy } from '@loadable/component';
import { array, predicate, record, string } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { IntlProvider, ReactIntlErrorCode } from 'react-intl';
import { polyfillIntl } from './polyfills';

declare global {
    namespace FormatjsIntl {
        interface Message {
            ids: Mews.TextKey;
        }
    }
}

const Polyfills = loadable.lib(async () => {
    await polyfillIntl();
    return Promise.resolve({});
});

const LazyPolyfills = lazy.lib(async () => {
    await polyfillIntl();
    return Promise.resolve({});
});

interface IntlProviderProps {
    children: React.ReactNode;
    languageCode: string;
    messagesWithDefaults: Record<Mews.TextKey, string>;
    lazy?: boolean;
}

const missingTranslations = new Set<string>();
const handleError: OnErrorFn = (error) => {
    if (process.env.NODE_ENV !== 'development') {
        return;
    }
    if (error.code === ReactIntlErrorCode.MISSING_TRANSLATION && error.descriptor?.id != null) {
        if (!missingTranslations.has(error.descriptor.id)) {
            missingTranslations.add(error.descriptor.id);
            // eslint-disable-next-line no-console
            console.error(error.message);
        }
        return;
    }
    // eslint-disable-next-line no-console
    console.error(error);
};

const filterOutEmptyMessages = record.filter(predicate.not(string.isEmpty));

export const createIntlLocalizationProvider = () => {
    const createMessagesWithDefaults = (allTexts: Record<string, Record<string, string>>, languageChain: string[]) => {
        const messagesWithDefaults = pipe(
            languageChain,
            array.reverse,
            array.map((lang) => filterOutEmptyMessages(allTexts[lang])),
            (messages) => Object.assign({}, ...messages)
        );
        return messagesWithDefaults;
    };

    const Provider = ({ messagesWithDefaults, languageCode, children, lazy: lazyLoaded }: IntlProviderProps) => {
        const Wrapper = lazyLoaded ? LazyPolyfills : Polyfills;
        return (
            <Wrapper>
                {() => (
                    <IntlProvider locale={languageCode} messages={messagesWithDefaults} onError={handleError}>
                        {children}
                    </IntlProvider>
                )}
            </Wrapper>
        );
    };
    return { Provider, createMessagesWithDefaults };
};

export { IntlProvider as InternalIntlProvider } from 'react-intl';
